import React from 'react';

interface GeographicDistribution {
  country: string;
  countryName: string;
  patronCount: number;
  revenue: number;
  percentage: number;
  latitude: number | null;
  longitude: number | null;
}

interface RevenueByRegionChartProps {
  data: GeographicDistribution[];
  limit?: number;
}

/**
 * Chart showing revenue breakdown by geographic region
 */
export const RevenueByRegionChart: React.FC<RevenueByRegionChartProps> = ({
  data,
  limit = 10,
}) => {
  // Sort by revenue and limit
  const sortedData = [...data]
    .sort((a, b) => b.revenue - a.revenue)
    .slice(0, limit);

  const totalRevenue = sortedData.reduce((sum, d) => sum + d.revenue, 0);
  const maxRevenue = Math.max(...sortedData.map((d) => d.revenue), 1);

  // Color palette for regions
  const colors = [
    'from-blue-500 to-blue-600',
    'from-purple-500 to-purple-600',
    'from-pink-500 to-pink-600',
    'from-red-500 to-red-600',
    'from-orange-500 to-orange-600',
    'from-yellow-500 to-yellow-600',
    'from-green-500 to-green-600',
    'from-teal-500 to-teal-600',
    'from-cyan-500 to-cyan-600',
    'from-indigo-500 to-indigo-600',
  ];

  if (data.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow-md border border-gray-200 p-8 text-center">
        <p className="text-gray-500">No revenue data available</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-md border border-gray-200 p-6">
      <div className="mb-6">
        <h3 className="text-lg font-semibold text-gray-900">
          Revenue by Region
        </h3>
        <p className="text-sm text-gray-500 mt-1">
          Top {sortedData.length} countries by monthly revenue
        </p>
      </div>

      {/* Total revenue summary */}
      <div className="mb-6 p-4 bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-lg">
        <div className="flex items-baseline justify-between">
          <span className="text-sm font-medium text-gray-700">
            Total Revenue (Top {sortedData.length})
          </span>
          <span className="text-2xl font-bold text-green-700">
            ${totalRevenue.toLocaleString(undefined, {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}
          </span>
        </div>
      </div>

      {/* Horizontal bar chart */}
      <div className="space-y-3">
        {sortedData.map((region, index) => (
          <div key={region.country} className="group">
            <div className="flex items-center justify-between mb-1">
              <div className="flex items-center gap-2 min-w-0">
                <span className="text-xs font-medium text-gray-400 w-6">
                  #{index + 1}
                </span>
                <span className="text-sm font-medium text-gray-900 truncate">
                  {region.countryName}
                </span>
              </div>
              <div className="flex items-center gap-3 ml-4 flex-shrink-0">
                <span className="text-xs text-gray-600">
                  {region.patronCount} patron{region.patronCount !== 1 ? 's' : ''}
                </span>
                <span className="text-sm font-semibold text-gray-900 min-w-[5rem] text-right">
                  ${region.revenue.toLocaleString(undefined, {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
                </span>
              </div>
            </div>
            <div className="relative w-full bg-gray-200 rounded-full h-4 overflow-hidden">
              <div
                className={`bg-gradient-to-r ${colors[index % colors.length]} h-4 rounded-full transition-all duration-500 ease-out group-hover:opacity-90 relative`}
                style={{
                  width: `${(region.revenue / maxRevenue) * 100}%`,
                }}
              >
                {/* Percentage label inside bar if wide enough */}
                {region.revenue / maxRevenue > 0.15 && (
                  <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs font-semibold text-white">
                    {region.percentage.toFixed(1)}%
                  </span>
                )}
              </div>
              {/* Percentage label outside bar if too narrow */}
              {region.revenue / maxRevenue <= 0.15 && (
                <span className="absolute left-[calc(100%+8px)] top-1/2 -translate-y-1/2 text-xs font-semibold text-gray-600">
                  {region.percentage.toFixed(1)}%
                </span>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Revenue insights */}
      {sortedData.length > 0 && (
        <div className="mt-6 p-4 bg-amber-50 border border-amber-200 rounded-lg">
          <div className="flex items-start gap-3">
            <svg
              className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <div>
              <p className="text-sm font-semibold text-amber-900">
                Top revenue source
              </p>
              <p className="text-xs text-amber-700 mt-1">
                <strong>{sortedData[0].countryName}</strong> generates{' '}
                {sortedData[0].percentage.toFixed(1)}% of your revenue with{' '}
                {sortedData[0].patronCount} patron{sortedData[0].patronCount !== 1 ? 's' : ''}.
                {sortedData.length > 1 && (
                  <> Followed by <strong>{sortedData[1].countryName}</strong> at{' '}
                    {sortedData[1].percentage.toFixed(1)}%.</>
                )}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RevenueByRegionChart;
